import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, KeyRound, UserCheck, UserX, Trash2, Edit, Lock } from 'lucide-react';
import api from '../lib/api';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [showTempPw, setShowTempPw] = useState(false);
  const [showSetPw, setShowSetPw] = useState(false);
  const [setPwValue, setSetPwValue] = useState('');
  const [setPwSaving, setSetPwSaving] = useState(false);
  const [form, setForm] = useState({ email: '', role_id: '', employee_id: '' });
  const [editForm, setEditForm] = useState({ email: '', role_id: '', employee_id: '', is_active: true });

  const load = async () => {
    try {
      const [uRes, rRes, eRes] = await Promise.all([
        api.get('/users'),
        api.get('/roles'),
        api.get('/employees'),
      ]);
      setUsers(uRes.data);
      setRoles(rRes.data);
      setEmployees(eRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      const res = await api.post('/users', {
        email: form.email,
        role_id: form.role_id,
        employee_id: form.employee_id || null,
      });
      setShowCreate(false);
      setTempPassword(res.data.temp_password);
      setShowTempPw(true);
      setForm({ email: '', role_id: '', employee_id: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await api.post(`/users/${userId}/reset-password`);
      setTempPassword(res.data.temp_password);
      setShowTempPw(true);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openSetPassword = (u) => { setSelectedUser(u); setSetPwValue(''); setShowSetPw(true); };
  const handleSetPassword = async () => {
    if (!setPwValue) return;
    setSetPwSaving(true);
    try {
      await api.post(`/users/${selectedUser.id}/set-password`, { new_password: setPwValue });
      setShowSetPw(false);
      setSetPwValue('');
      alert(`Password impostata per ${selectedUser.email}. Comunicagliela per accedere.`);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
    finally { setSetPwSaving(false); }
  };

  const handleToggleActive = async (userId, currentActive) => {
    await api.put(`/users/${userId}`, { is_active: !currentActive });
    load();
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email,
      role_id: user.role_id,
      employee_id: user.employee_id || '',
      is_active: user.is_active
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/users/${selectedUser.id}`, {
        email: editForm.email,
        role_id: editForm.role_id,
        employee_id: editForm.employee_id || null,
        is_active: editForm.is_active
      });
      setShowEdit(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDelete(true);
  };

  const confirmDelete = async (permanent) => {
    try {
      await api.delete(`/users/${selectedUser.id}?permanent=${permanent}`);
      setShowDelete(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Gestione Utenti</h1>
        <Button onClick={() => setShowCreate(true)} data-testid="add-user-btn">
          <Plus size={16} className="mr-1" /> Nuovo Utente
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead className="hidden sm:table-cell">Dipendente</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="hidden md:table-cell">Ultimo Login</TableHead>
                <TableHead>Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Nessun utente</TableCell></TableRow>
              ) : users.map(u => (
                <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{u.email}</p>
                      {u.must_change_password && <p className="text-[10px] text-amber-600">Cambio password richiesto</p>}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">{u.role_display_name}</Badge></TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-slate-600">{u.employee_name || '-'}</TableCell>
                  <TableCell>
                    {u.is_active ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><UserCheck size={12} className="mr-1" />Attivo</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><UserX size={12} className="mr-1" />Inattivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-slate-500">
                    {u.last_login ? new Date(u.last_login).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Mai'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(u)} title="Modifica" data-testid={`edit-user-${u.id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u.id)} title="Reset Password (temporanea)" data-testid={`reset-pw-${u.id}`}>
                        <KeyRound size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openSetPassword(u)} title="Imposta password manuale" className="text-blue-600 hover:text-blue-700" data-testid={`set-pw-${u.id}`}>
                        <Lock size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(u.id, u.is_active)}
                        className={u.is_active ? 'text-amber-600 hover:text-amber-700' : 'text-green-600 hover:text-green-700'}
                        title={u.is_active ? 'Disattiva' : 'Riattiva'}
                        data-testid={`toggle-active-${u.id}`}
                      >
                        {u.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(u)}
                        className="text-red-600 hover:text-red-700"
                        title="Elimina"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuovo Utente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Ruolo *</Label>
              <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Seleziona ruolo..." /></SelectTrigger>
                <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.display_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Collega Dipendente (opzionale)</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="user-emp-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {employees.filter(e => e.is_active).map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.email || !form.role_id} data-testid="user-save-btn">Crea Utente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Dialog */}
      <Dialog open={showTempPw} onOpenChange={setShowTempPw}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Password Temporanea</DialogTitle></DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <p className="text-sm text-amber-800 mb-2">Comunica questa password all'utente:</p>
            <p className="text-lg font-mono font-bold text-amber-900" data-testid="temp-password-display">{tempPassword}</p>
            <p className="text-xs text-amber-600 mt-2">L'utente dovra cambiarla al primo accesso</p>
          </div>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard.writeText(tempPassword); setShowTempPw(false); }}>Copia e Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog (admin imposta password manuale) */}
      <Dialog open={showSetPw} onOpenChange={setShowSetPw}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Imposta Password</DialogTitle>
            <DialogDescription>Imposta una password per {selectedUser?.email}. L'utente potrà accedere subito con questa password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nuova password</Label>
            <Input
              type="text"
              value={setPwValue}
              onChange={(e) => setSetPwValue(e.target.value)}
              placeholder="Es. Fiumicino2026!"
              data-testid="set-pw-input"
            />
            <p className="text-xs text-slate-500">Minimo 8 caratteri, 1 maiuscola, 1 numero, 1 simbolo.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPw(false)}>Annulla</Button>
            <Button onClick={handleSetPassword} disabled={setPwSaving || !setPwValue} data-testid="set-pw-save">{setPwSaving ? 'Salvataggio...' : 'Imposta Password'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifica Utente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} type="email" data-testid="edit-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Ruolo *</Label>
              <Select value={editForm.role_id} onValueChange={(v) => setEditForm({ ...editForm, role_id: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue placeholder="Seleziona ruolo..." /></SelectTrigger>
                <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.display_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Collega Dipendente</Label>
              <Select value={editForm.employee_id || 'none'} onValueChange={(v) => setEditForm({ ...editForm, employee_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="edit-user-emp"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {employees.filter(e => e.is_active).map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="edit-active" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="rounded" />
              <Label htmlFor="edit-active">Utente attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.email || !editForm.role_id} data-testid="edit-user-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Elimina Utente</DialogTitle>
            <DialogDescription>Scegli come procedere con l'utente {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button variant="outline" className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => confirmDelete(false)}>
              <UserX size={16} className="mr-2" /> Disattiva (conserva dati)
            </Button>
            <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => confirmDelete(true)}>
              <Trash2 size={16} className="mr-2" /> Elimina definitivamente
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDelete(false)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
